"use client";

import { authApi } from "@/apis";
import { LoginSchema } from "@/lib/validate";
import { authActions } from "@/store/actions";
import { ETypeOTP } from "@/types/enum";
import { FormikProps, useFormik } from "formik";
import { useRouter } from "next/navigation";
import { useState } from "react";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { SliderCaptchaBox } from "../custom/slider-captcha";
import { BaseButton } from "../share/button";
import { BaseInput, InputPasswordField } from "../share/input";
import { useGlobalModal } from "./modal-provider";
import { ModalOtp } from "./ModalOtp";
import { disconnect } from "@wagmi/core";

interface Props {
  onSuccess?: () => void;
}

export interface IFormLogin {
  email: string;
  password: string;
  captchaToken: string;
}

const initialValues: IFormLogin = {
  email: "",
  password: "",
  captchaToken: "",
};

export const LoginModal = ({ onSuccess }: Props) => {
  const { openModal } = useGlobalModal();
  const dispatch = useDispatch<any>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const router = useRouter();

  const formik: FormikProps<IFormLogin> = useFormik({
    initialValues: initialValues,
    validationSchema: LoginSchema,
    validateOnChange: false,
    onSubmit: async (values: IFormLogin) => {
      await disconnect();
      await handleLogin(values);
    },
  });

  const handleLogin = async (data: IFormLogin) => {
    const { email, password, captchaToken } = data;

    setLoading(true);
    try {
      const { userId, email: preEmail } = await authApi.preLogin({
        email: email,
        password: password,
        captchaToken,
      });
      dispatch(
        authActions.preLoginSuccess({ email: preEmail, userId: userId })
      );
      dispatch(
        authActions.otpSend(
          {
            type: ETypeOTP.Email,
            userId,
            scenario: "login",
          },
          () => {
            openModal({
              title: "Xác nhận OTP",
              description: `Nhập mã chúng tôi vừa nhắn tin đến email ${formik?.values?.email}`,
              body: <ModalOtp password={password} />,
            });
            setLoading(false);
          },
          () => {
            setLoading(false);
          }
        )
      );
    } catch (err) {
      setError(err?.message);
      toast.error(err?.message);
      setLoading(false);
    }
  };

  return (
    <div>
      <form className="flex flex-col gap-4" onSubmit={formik.handleSubmit}>
        <BaseInput
          title={"Email"}
          type="text"
          placeholder="example@domain.com"
          value={formik?.values?.email}
          onChange={(e) => formik.setFieldValue("email", e.target.value)}
          error={formik?.errors?.email}
        />
        <InputPasswordField
          formik={formik}
          label="Mật khẩu"
          name="password"
          labelClass="text-sm text-purple_700 font-medium"
          className="h-12 py-3 pl-4 pr-4 mt-2 text-base rounded-md"
          errClass="mb-1 mt-2"
          layoutClass="input-focus input-hover"
          placeholder="Mật khẩu"
          messageError={formik?.errors?.password}
        />

        <SliderCaptchaBox
          onVerifySuccess={(token) => {
            formik.setFieldValue("captchaToken", token);
          }}
          warning={
            !!(formik.touched["captchaToken"] && formik.errors["captchaToken"])
          }
        />
        <BaseButton className="w-full h-12" type="submit">
          Đăng nhập
        </BaseButton>
      </form>
    </div>
  );
};
